import { useMemo, useState } from 'react';
import { ArrowLeft, Bookmark, Pencil, Plus, RefreshCw, Trash2, X } from 'lucide-react';
import { MEAL_GROUPS } from '../state';
import { entriesToTemplateItems, entryMacros, foodPortion, isMassUnit, templateMacros } from '../food';
import { FONT_MONO } from '../theme';
import { Button, Input, NumberInput } from '../ui';

const SCALES = [0.5, 1, 1.5];

const iconBtn = (t) => ({
  flexShrink: 0, width: 44, background: 'transparent', border: 'none', cursor: 'pointer',
  color: t.textFaint, display: 'flex', alignItems: 'center', justifyContent: 'center',
});

/* Save the current group as a template, or log a saved one in a single tap.
 * Templates remember the group they came from and that group's sort first,
 * but any template can be applied anywhere -- leftovers move meals.
 * The scale (x0.5 / x1 / x1.5) multiplies each item's portion when applied,
 * never its per-unit macros. `dayText` names the shown day ('today' or a
 * date) for "Replace with today's breakfast". */
export function MealTemplateSheet({ t, group, groupLabel, dayText = 'today', templates, currentEntries, onApply, onSave, onUpdate, onDelete, onClose }) {
  const [name, setName] = useState('');
  const [scale, setScale] = useState(1);
  const [draft, setDraft] = useState(null); // { id, name, items } while editing one
  const canSave = (currentEntries || []).length > 0;

  const sorted = useMemo(() => [...(templates || [])].sort((a, b) => {
    const own = (a.group === group ? 0 : 1) - (b.group === group ? 0 : 1);
    return own || String(b.createdAt || '').localeCompare(String(a.createdAt || ''));
  }), [templates, group]);

  const submitSave = () => {
    const clean = name.trim();
    if (!clean || !canSave) return;
    onSave(clean);
    setName('');
  };

  const startEdit = (tpl) => setDraft({ id: tpl.id, name: tpl.name || '', items: (tpl.items || []).map(it => ({ ...it })) });
  const setItem = (i, patch) => setDraft(d => ({ ...d, items: d.items.map((it, j) => j === i ? { ...it, ...patch } : it) }));
  const canSubmitEdit = draft && draft.name.trim() && draft.items.length > 0 && draft.items.every(it => Number(it.portion) > 0);
  const submitEdit = () => {
    if (!canSubmitEdit) return;
    onUpdate(draft.id, { name: draft.name, items: draft.items.map(it => ({ ...it, portion: Number(it.portion) })) });
    setDraft(null);
  };

  const shell = (title, body, footer) => (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: t.cardBg, borderTopLeftRadius: 16, borderTopRightRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 -8px 32px rgba(0,0,0,0.30)' }}>
        <div style={{ padding: '10px 10px 10px 18px', borderBottom: `1px solid ${t.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          {title}
          <button onClick={onClose} aria-label="Close" style={{ ...iconBtn(t), height: 40 }}><X size={18} /></button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>{body}</div>
        <div style={{ borderTop: `1px solid ${t.divider}`, padding: '12px 18px', paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}>{footer}</div>
      </div>
    </div>
  );

  if (draft) {
    const m = templateMacros(draft);
    return shell(
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
        <button onClick={() => setDraft(null)} aria-label="Back to templates" style={{ ...iconBtn(t), height: 40, width: 36, marginLeft: -10, color: t.textMuted }}>
          <ArrowLeft size={18} />
        </button>
        <span style={{ fontSize: 16, fontWeight: 700, color: t.text }}>Edit template</span>
      </div>,
      <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Input t={t} aria-label="Template name" placeholder="Template name..." value={draft.name} maxLength={60}
          onChange={e => { const v = e.target.value; setDraft(d => ({ ...d, name: v })); }}
          style={{ fontSize: 14, padding: '10px 12px' }} />
        <div style={{ fontSize: 11, color: t.textFaint, fontFamily: FONT_MONO }}>
          {draft.items.length} item{draft.items.length === 1 ? '' : 's'} · {Math.round(m.kcal)} kcal · {Math.round(m.protein)}P {Math.round(m.carbs)}C {Math.round(m.fats)}F
        </div>
        <div style={{ border: `1px solid ${t.cardBorder}`, borderRadius: 10 }}>
          {draft.items.length === 0 && (
            <div style={{ padding: '14px 12px', fontSize: 12, color: t.textFaint }}>No items left. Add one back with Replace, or cancel.</div>
          )}
          {draft.items.map((it, i) => {
            const im = entryMacros({ per: it.per, amount: Number(it.portion) || 0 });
            const mass = isMassUnit(it.unit);
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0 8px 12px', borderTop: i ? `1px solid ${t.divider}` : 'none' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, color: t.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.name}</div>
                  <div style={{ fontSize: 11, color: t.textFaint, fontFamily: FONT_MONO, marginTop: 2 }}>
                    {Math.round(im.kcal)} kcal · {Math.round(im.protein)}P
                  </div>
                </div>
                <div style={{ width: 84, flexShrink: 0 }}>
                  <NumberInput t={t} value={it.portion ?? foodPortion(it)} min={0} step={mass ? 10 : 0.5}
                    suffix={mass ? it.unit : '×'} onChange={v => setItem(i, { portion: v })} />
                </div>
                <button onClick={() => setDraft(d => ({ ...d, items: d.items.filter((_, j) => j !== i) }))}
                  aria-label={`Remove ${it.name}`} title="Remove item" style={{ ...iconBtn(t), height: 40 }}>
                  <X size={16} />
                </button>
              </div>
            );
          })}
        </div>
        {canSave && (
          <Button t={t} onClick={() => setDraft(d => ({ ...d, items: entriesToTemplateItems(currentEntries) }))}
            style={{ justifyContent: 'center', minHeight: 40, whiteSpace: 'normal', textAlign: 'center' }}>
            <RefreshCw size={14} />
            {dayText === 'today'
              ? `Replace with today’s ${groupLabel.toLowerCase()} (${currentEntries.length})`
              : `Replace with the ${groupLabel.toLowerCase()} of ${dayText} (${currentEntries.length})`}
          </Button>
        )}
      </div>,
      <div style={{ display: 'flex', gap: 8 }}>
        <Button t={t} onClick={() => setDraft(null)} style={{ minHeight: 40 }}>Cancel</Button>
        <Button t={t} variant="primary" disabled={!canSubmitEdit} onClick={submitEdit} style={{ flex: 1, justifyContent: 'center', minHeight: 40 }}>
          Save changes
        </Button>
      </div>,
    );
  }

  return shell(
    <div style={{ fontSize: 16, fontWeight: 700, color: t.text, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <Bookmark size={16} /> {groupLabel} templates
    </div>,
    sorted.length === 0 ? (
      <div style={{ padding: '28px 24px', textAlign: 'center', color: t.textMuted, fontSize: 13, lineHeight: 1.5 }}>
        No templates yet. Log a meal you repeat often, then save it here to add the whole thing in one tap.
      </div>
    ) : (
      <>
        {/* Amount of every item when applied: half a portion of leftovers, a bigger plate after legs */}
        <div style={{ padding: '10px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, borderBottom: `1px solid ${t.divider}` }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Portion</span>
          <div role="radiogroup" aria-label="Portion when applied" style={{ display: 'flex', gap: 4 }}>
            {SCALES.map(s => {
              const on = s === scale;
              return (
                <button key={s} role="radio" aria-checked={on} onClick={() => setScale(s)} style={{
                  minWidth: 52, height: 40, borderRadius: 10, cursor: 'pointer', fontFamily: FONT_MONO, fontSize: 13, fontWeight: 700,
                  background: on ? t.accentSoft : 'transparent', color: on ? t.accent : t.textMuted,
                  border: `1px solid ${on ? t.accentBorder : t.cardBorder}`,
                }}>×{s}</button>
              );
            })}
          </div>
        </div>
        {sorted.map(tpl => {
          const m = templateMacros(tpl, scale);
          const other = MEAL_GROUPS.find(g => g.id === tpl.group);
          const foreign = tpl.group && tpl.group !== group && other;
          const count = (tpl.items || []).length;
          return (
            <div key={tpl.id} style={{ display: 'flex', alignItems: 'stretch', borderBottom: `1px solid ${t.divider}` }}>
              <button onClick={() => { onApply(tpl, scale); onClose(); }} style={{
                flex: 1, minWidth: 0, textAlign: 'left', padding: '11px 8px 11px 18px',
                background: 'transparent', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
              }}
                onMouseEnter={e => e.currentTarget.style.background = t.surfaceAlt}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 14, color: t.text, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tpl.name}</span>
                    {foreign && (
                      <span style={{ flexShrink: 0, fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: t.textFaint, border: `1px solid ${t.cardBorder}`, borderRadius: 5, padding: '1px 5px' }}>{other.label}</span>
                    )}
                  </span>
                  <span style={{ fontSize: 11, color: t.textFaint, fontFamily: FONT_MONO, display: 'block', marginTop: 2 }}>
                    {count} item{count === 1 ? '' : 's'} &middot; {Math.round(m.kcal)} kcal &middot; {Math.round(m.protein)}P {Math.round(m.carbs)}C {Math.round(m.fats)}F
                  </span>
                </span>
                <Plus size={16} color={t.accent} style={{ flexShrink: 0 }} />
              </button>
              <button onClick={() => startEdit(tpl)} aria-label={`Edit ${tpl.name}`} title="Edit template" style={iconBtn(t)}>
                <Pencil size={14} />
              </button>
              <button onClick={() => onDelete(tpl.id)} aria-label={`Delete ${tpl.name}`} title="Delete template" style={{ ...iconBtn(t), width: 40, paddingRight: 4 }}>
                <Trash2 size={14} />
              </button>
            </div>
          );
        })}
      </>
    ),
    canSave ? (
      <>
        <div style={{ fontSize: 11, fontWeight: 700, color: t.textFaint, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 8 }}>
          Save this {groupLabel.toLowerCase()} ({currentEntries.length} item{currentEntries.length === 1 ? '' : 's'})
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input t={t} placeholder="Template name..." value={name} maxLength={60}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') submitSave(); }}
            style={{ flex: 1 }} />
          <Button t={t} variant="primary" disabled={!name.trim()} onClick={submitSave} style={{ flexShrink: 0, minHeight: 40 }}>
            <Bookmark size={14} /> Save
          </Button>
        </div>
      </>
    ) : (
      <div style={{ fontSize: 12, color: t.textFaint, lineHeight: 1.5 }}>
        Add some food to {groupLabel.toLowerCase()} first, then come back here to save it as a template.
      </div>
    ),
  );
}
